import { useRouter } from 'next/router';

import {
  Button,
  Image,
  Paragraph,
  Link,
  Text,
  Header,
  Center,
  DarkMode,
  LightMode,
  useToken,
  readableColor,
  lighten,
  rem,
} from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';
import { useViewer } from '@vimeo/viewer';

import { type AlbumMetadata } from '../hooks/use-showcase-auth';
import { useAlbumStore } from '../state/albumStore';

export const BrandedLoginPage = ({
  name,
  customLogo,
  brandColor,
  theme,
  hasSso,
  ssoConnectionsCount,
  url,
  id,
  creator,
}: AlbumMetadata) => {
  const { isShowcaseEmbedded, isOffsite } = useAlbumStore((state) => ({
    isShowcaseEmbedded: state.isShowcaseEmbedded,
    isOffsite: state.isOffsite,
  }));

  /** Styling */
  const accentColor = brandColor ? `#${brandColor}` : null;
  const [fillBrandColor] = useToken('colors', ['fill-brand']);
  const ColorModeWrapper = theme === 'dark' ? DarkMode : LightMode;

  /** Copy */
  const multipleIds = ssoConnectionsCount > 1;
  const ssoEnabled = hasSso && !multipleIds;
  const buttonText = ssoEnabled ? translate('Log in with SSO') : translate('Log in');

  /** Signin url */
  const router = useRouter();
  const viewer = useViewer();
  const vimeoHttpsUrl = viewer && viewer.vimeoHttpsUrl;

  const albumLookup = url ? url : id;
  const offsitePath = router?.query?.offsite === 'redirect' ? '/offsite_redirect' : '?app=showcase';

  const prevPageUrl = isOffsite
    ? encodeURIComponent(`${vimeoHttpsUrl}/showcase/${albumLookup}${offsitePath}`)
    : `${vimeoHttpsUrl}/showcase/${albumLookup}`;

  const handleLogin = async (useSSO = true) => {
    const getToken = () =>
      new Promise<void>((resolve, reject) => {
        const messageListener = (event: { data: { type: string }; origin: string }) => {
          try {
            // Validate the origin of the message
            if (event.origin !== vimeoHttpsUrl) {
              return; // Ignore messages from unexpected origins
            }
            if (event.data.type && event.data.type === 'success') {
              resolve();
            } else {
              reject(new Error('Login failed'));
            }
          } finally {
            window.removeEventListener('message', messageListener);
          }
        };
        window.addEventListener('message', messageListener);
      });

    const loginWindow = window.open(
      `/showcase/${id}/authorize?use_sso=${useSSO}`,
      '',
      'width=800,height=600,menubar=no,scrollbars=no,titlebar=no,toolbar=no',
    );

    try {
      await getToken();
      loginWindow?.close();
      location.reload();
    } catch (error) {
      console.error(error.message);
      loginWindow?.close();
    }
  };

  const onLoginButtonClick = () => {
    if (isShowcaseEmbedded) {
      handleLogin(ssoEnabled);
      return;
    }

    const signInUrl = ssoEnabled
      ? `${vimeoHttpsUrl}/auth0/connect?prev_page=${prevPageUrl}&team_id=${creator}`
      : `${vimeoHttpsUrl}/log_in?redirect=${prevPageUrl}`;

    window.location.href = signInUrl;
  };

  return (
    <ColorModeWrapper>
      <Center h="100vh" minH={rem(400)} w="100%" bgColor="background" flexFlow="column" p="1rem">
        <Center
          maxWidth={rem(520)}
          paddingX={rem(80)}
          paddingY={rem(60)}
          backgroundColor="fill-surface"
          flexFlow="column"
          borderRadius="xl"
          textAlign="center"
        >
          {customLogo ? (
            <Image
              src={customLogo}
              alt={name}
              marginX="auto"
              marginBottom={rem(36)}
              display="block"
              maxWidth={rem(260)}
              width="auto"
              maxHeight={rem(200)}
              height="auto"
            />
          ) : null}

          <Header variant="heading-lg" marginBottom={rem(36)} color="text-primary" size="xl">
            {translate({
              singular: 'Log in to {TITLE}',
              replacements: {
                TITLE: name,
              },
            })}
          </Header>

          <Text variant="body-md" color="text-secondary" marginBottom="200" textAlign="center">
            {translate({
              singular:
                'By logging in to Vimeo, you agree to our {TERMS}Terms of Service{/TERMS} and acknowledge our {PRIVACY}Privacy Policy{/PRIVACY}.',
              replacements: {
                TERMS: (text: string) => (
                  <Link
                    key="terms"
                    href="/terms"
                    target="_blank"
                    variant="inline-primary"
                    fontSize="inherit"
                    color="inherit"
                  >
                    {text}
                  </Link>
                ),
                PRIVACY: (text: string) => (
                  <Link
                    key="privacy"
                    href="/privacy"
                    target="_blank"
                    variant="inline-primary"
                    fontSize="inherit"
                    color="inherit"
                  >
                    {text}
                  </Link>
                ),
              },
            })}
          </Text>

          <Button
            size="lg"
            {...(accentColor && {
              bgColor: accentColor,
              color: readableColor(accentColor),
              _hover: {
                bgColor: lighten(accentColor, 0.1),
              },
              _dark: {
                bgColor: accentColor,
                _hover: {
                  bgColor: lighten(accentColor, 0.1),
                },
              },
            })}
            onClick={onLoginButtonClick}
          >
            {buttonText}
          </Button>

          {ssoEnabled ? (
            <Paragraph variant="body-md" marginTop="200" color="text-primary">
              {translate({
                singular: '{A}Log in{/A} with email and password',
                replacements: {
                  A: (children: React.ReactNode) => (
                    <Link
                      key="showcase-login-with-email"
                      href="#"
                      style={{ color: accentColor ?? fillBrandColor }}
                      onClick={() => handleLogin(false)}
                      variant="inline-primary"
                      fontSize="inherit"
                    >
                      {children}
                    </Link>
                  ),
                },
              })}
            </Paragraph>
          ) : null}
        </Center>
      </Center>
    </ColorModeWrapper>
  );
};
